import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  forwardRef,
} from '@angular/core';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  FormControl,
} from '@angular/forms';

export type ButtonLength = 'full' | 'half' | 'quarter';
export type ButtonAlignment = 'left' | 'center' | 'right';
export type ButtonStyle = 'filled' | 'outlined' | 'text';

@Component({
  standalone: false,
  selector: 'irembogov-generic-button',
  templateUrl: './irembogov-generic-button.component.html',
  styleUrls: ['./irembogov-generic-button.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => IremboGenericButtonComponent),
      multi: true,
    },
  ],
})
export class IremboGenericButtonComponent implements ControlValueAccessor {
  @Input() id: unknown;
  @Input() buttonText!: string;
  @Input() buttonStyle: ButtonStyle = 'filled';
  @Input() statusClass: string | undefined;
  @Input() disabled = false;
  @Input() width?: number;
  @Input() alignment: ButtonAlignment = 'left';
  @Output() buttonClick = new EventEmitter<void>();

  customFormControl = new FormControl();
  private onTouchedCallback: () => void = () => {};

  get containerClasses(): string {
    const alignmentClasses = {
      left: 'justify-content-start',
      center: 'justify-content-center',
      right: 'justify-content-end',
    };

    return `d-flex ${
      alignmentClasses[this.alignment] || 'justify-content-start'
    }`;
  }

  get buttonClasses(): string {
    const styleClasses = {
      filled: 'btn btn-primary',
      outlined: 'btn btn-outline-primary',
      text: 'text-button',
    };

    const widthClass = this.width ? `w-${this.width}` : 'w-100';

    return `
      ${styleClasses[this.buttonStyle] || 'btn btn-primary'}
      ${this.buttonStyle !== 'text' ? widthClass : ''}
      ${this.statusClass === 'fetching' ? 'fetching' : ''}
    `.trim();
  }

  onClick(): void {
    if (!this.disabled) {
      this.onTouchedCallback();
      this.buttonClick.emit();
    }
  }

  writeValue(obj: unknown): void {
    this.customFormControl.setValue(obj);
  }

  registerOnChange(fn: any): void {
    this.customFormControl.valueChanges.subscribe(fn);
  }

  registerOnTouched(fn: any): void {
    this.onTouchedCallback = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    if (isDisabled) {
      this.customFormControl.disable();
    } else {
      this.customFormControl.enable();
    }
  }
}
